import { CloudOff, Cloud } from 'lucide-react';
import { useOnlineStatus } from '@/lib/use-online-status';
import { cn } from '@/lib/utils';

/**
 * Small status pill anchored above the bottom nav on mobile, above
 * the safe-area on desktop. Renders only when offline or briefly
 * after a reconnect — silent the rest of the time.
 *
 * Terracotta-on-cream for offline (matches the field-binder
 * "needs attention" tone). Forest-green-on-cream for the brief
 * "Reconnected" flash.
 */
export default function OnlineIndicator() {
  const { online, justReconnected } = useOnlineStatus();
  if (online && !justReconnected) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="online-indicator"
      className={cn(
        'fixed inset-x-0 z-30 flex justify-center pointer-events-none',
        // Sit just above the bottom nav on mobile; bottom-right
        // corner on desktop.
        'bottom-[calc(72px+env(safe-area-inset-bottom))] md:bottom-4 md:inset-x-auto md:right-4',
      )}
    >
      <span
        className={cn(
          'pointer-events-auto inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium shadow-sm',
          online
            ? 'border-primary/30 bg-card text-primary'
            : 'border-warning/40 bg-card text-warning',
        )}
      >
        {online ? <Cloud className="h-3.5 w-3.5" aria-hidden /> : <CloudOff className="h-3.5 w-3.5" aria-hidden />}
        {online ? 'Reconnected' : 'Offline — using cached data'}
      </span>
    </div>
  );
}
